import React, { useMemo, useState } from 'react';
import { BuildingOfficeIcon } from '@heroicons/react/24/outline';
import { AxiosResponse } from 'axios';
import { Link } from 'react-router-dom';
import { useQuery } from 'react-query';
import { platformAPI } from '../services/api';
import {
  buildTenantBaseUrl,
  SupportTenantOption,
} from '../utils/supportTenantAccess';

type SupportTenantSwitcherProps = {
  placement?: 'navbar' | 'sidebar';
};

function SupportTenantSwitcher({ placement = 'sidebar' }: SupportTenantSwitcherProps) {
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const isNavbar = placement === 'navbar';

  const { data: loadedTenants, isLoading } = useQuery<AxiosResponse<any>, Error, SupportTenantOption[]>(
    ['support-accessible-tenant-companies'],
    () => platformAPI.getSupportTenantSwitchCompanies(),
    {
      select: (response) => {
        const data = response.data;
        if (Array.isArray(data)) return data as SupportTenantOption[];
        if (Array.isArray(data?.companies)) return data.companies as SupportTenantOption[];
        return [] as SupportTenantOption[];
      },
      staleTime: 60_000,
    },
  );

  const tenants = loadedTenants ?? [];

  const options = useMemo(
    () => tenants.filter((tenant) => tenant?.slug).map((tenant) => ({
      id: tenant.id,
      slug: tenant.slug,
      label: tenant.name || tenant.slug,
    })),
    [tenants],
  );
  const hasOptions = options.length > 0;

  const switchTenant = (tenantId: string) => {
    const tenant = options.find((item) => item.id === tenantId);
    if (!tenant) return;

    const targetBaseUrl = buildTenantBaseUrl(tenant.slug);
    const token = window.localStorage.getItem('token');
    const handoffUrl = new URL('/support/handoff', targetBaseUrl);
    handoffUrl.searchParams.set('next', '/admin');
    if (token) {
      handoffUrl.searchParams.set('token', token);
    }
    window.location.assign(handoffUrl.toString());
  };

  if (isNavbar) {
    return (
      <div className="hidden md:flex items-center gap-2 rounded-card border border-outline/60 bg-surface-container-low px-3 py-2">
        <BuildingOfficeIcon className="h-4 w-4 text-on-surface-variant" />
        <select
          value={selectedTenantId}
          onChange={(event) => {
            const tenantId = event.target.value;
            setSelectedTenantId('');
            if (tenantId) {
              switchTenant(tenantId);
            }
          }}
          className="min-w-[14rem] rounded-md border border-outline/60 bg-surface px-2 py-1.5 text-sm text-on-surface focus:border-primary focus:outline-none"
          disabled={isLoading || !hasOptions}
          aria-label="Switch support tenant"
        >
          <option value="">
            {isLoading ? 'Loading tenants...' : hasOptions ? 'Switch tenant...' : 'No tenants available'}
          </option>
          {options.map((tenant) => (
            <option key={tenant.id} value={tenant.id}>
              {tenant.label}
            </option>
          ))}
        </select>
        {!hasOptions && !isLoading && (
          <Link
            to="/support/tenant-switcher"
            className="text-xs font-semibold text-primary hover:underline"
          >
            Open portal
          </Link>
        )}
      </div>
    );
  }

  if (!hasOptions && !isLoading) {
    return null;
  }

  return (
    <div className="mb-3 rounded-lg border border-outline/60 bg-surface-container-low px-3 py-2">
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-on-surface-variant">
        Tenant Switcher
      </div>
      <select
        value={selectedTenantId}
        onChange={(event) => {
          const tenantId = event.target.value;
          setSelectedTenantId('');
          if (tenantId) {
            switchTenant(tenantId);
          }
        }}
        className="w-full rounded-md border border-outline/60 bg-surface px-2 py-1.5 text-sm text-on-surface focus:border-primary focus:outline-none"
        disabled={isLoading || !hasOptions}
      >
        <option value="">{isLoading ? 'Loading tenants...' : 'Switch to tenant...'}</option>
        {options.map((tenant) => (
          <option key={tenant.id} value={tenant.id}>
            {tenant.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export default SupportTenantSwitcher;